import { useState, useCallback } from "react";

/**
 * Shared date range state and handlers for Dashboard, Sessions, and Feedbacks.
 * @param { (iso: string, days: number, startTime: string, endTime: string) => void } onApply - Called when user applies a date range (quick filter or Apply with time).
 * @returns { object } State and handlers for DateRangePicker and for consumers (getCurrentRange, selectedEndDate, etc.)
 */
export function useDateRange(onApply) {
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [selectedDays, setSelectedDays] = useState(7);
  const [selectedStartTime, setSelectedStartTime] = useState("00:00:00");
  const [selectedEndTime, setSelectedEndTime] = useState("23:59:00");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [calendarSelectionDays, setCalendarSelectionDays] = useState(7);
  const [pendingDateForTime, setPendingDateForTime] = useState(null);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const getCurrentRange = useCallback(() => {
    let start, end;
    if (selectedEndDate) {
      end = new Date(selectedEndDate + "T12:00:00");
      start = new Date(end);
      start.setDate(end.getDate() - (selectedDays - 1));
    } else {
      const tmpEnd = new Date();
      const tmpStart = new Date(tmpEnd);
      tmpStart.setDate(tmpEnd.getDate() - 6);
      start = tmpStart;
      end = tmpEnd;
    }
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return { start, end };
  }, [selectedEndDate, selectedDays]);

  // Range used for calendar highlighting: when user has picked a date (pending), show that; otherwise show applied range
  const getDisplayRange = useCallback(() => {
    if (pendingDateForTime) {
      const end = new Date(pendingDateForTime + "T12:00:00");
      if (isNaN(end.getTime())) return getCurrentRange();
      const start = new Date(end);
      start.setDate(end.getDate() - (calendarSelectionDays - 1));
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      return { start, end };
    }
    return getCurrentRange();
  }, [pendingDateForTime, calendarSelectionDays, getCurrentRange]);

  const formatDayWithSuffix = (day) => {
    if (day >= 11 && day <= 13) return `${day}th`;
    const last = day % 10;
    if (last === 1) return `${day}st`;
    if (last === 2) return `${day}nd`;
    if (last === 3) return `${day}rd`;
    return `${day}th`;
  };

  const getDateRangeLabel = useCallback(() => {
    const { start, end } = getCurrentRange();
    if (selectedDays === 1) {
      return `${formatDayWithSuffix(end.getDate())} ${end.toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      })}`;
    }
    const startLabel = `${formatDayWithSuffix(start.getDate())} ${start.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    })}`;
    const endLabel = `${formatDayWithSuffix(end.getDate())} ${end.toLocaleString("en-US", {
      month: "long",
      year: "numeric",
    })}`;
    return `${startLabel} - ${endLabel}`;
  }, [getCurrentRange, selectedDays]);

  const buildMonthMatrix = useCallback((baseDate) => {
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startWeekday = firstDay.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const weeks = [];
    let currentDay = 1 - startWeekday;
    for (let w = 0; w < 6; w++) {
      const weekDays = [];
      for (let d = 0; d < 7; d++) {
        if (currentDay < 1 || currentDay > daysInMonth) weekDays.push(null);
        else weekDays.push(new Date(year, month, currentDay));
        currentDay++;
      }
      weeks.push(weekDays);
    }
    return weeks;
  }, []);

  const isDateInRange = useCallback(
    (date) => {
      if (!date) return false;
      const { start, end } = getDisplayRange();
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d >= start && d <= end;
    },
    [getDisplayRange]
  );

  const isRangeBoundary = useCallback(
    (date) => {
      if (!date) return false;
      const { start, end } = getDisplayRange();
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d.getTime() === start.getTime() || d.getTime() === end.getTime();
    },
    [getDisplayRange]
  );

  const handleCalendarDayClick = useCallback((date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const picked = new Date(date);
    picked.setHours(0, 0, 0, 0);
    if (picked > today) return;
    const year = picked.getFullYear();
    const month = String(picked.getMonth() + 1).padStart(2, "0");
    const day = String(picked.getDate()).padStart(2, "0");
    const iso = `${year}-${month}-${day}`;
    setSelectedStartTime("00:00:00");
    setSelectedEndTime("23:59:00");
    setPendingDateForTime(iso);
  }, []);

  const handleApplyDateWithTime = useCallback(() => {
    const iso = pendingDateForTime || (selectedEndDate && calendarSelectionDays === 1 ? selectedEndDate : null);
    if (!iso) return;
    const days = calendarSelectionDays;
    setSelectedEndDate(iso);
    setSelectedDays(days);
    if (typeof onApply === "function") onApply(iso, days, selectedStartTime, selectedEndTime);
    setIsDatePickerOpen(false);
    setPendingDateForTime(null);
    setCalendarSelectionDays(days);
  }, [pendingDateForTime, selectedEndDate, calendarSelectionDays, selectedStartTime, selectedEndTime, onApply]);

  const handleQuickDateFilter = useCallback(
    (days) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const actualDays = days;
      let endDate = today;
      if (actualDays === 1 && selectedEndDate) {
        const parsed = new Date(selectedEndDate + "T12:00:00");
        if (!isNaN(parsed.getTime()) && parsed <= today) endDate = parsed;
      }
      const year = endDate.getFullYear();
      const month = String(endDate.getMonth() + 1).padStart(2, "0");
      const day = String(endDate.getDate()).padStart(2, "0");
      const iso = `${year}-${month}-${day}`;
      setSelectedEndDate(iso);
      setSelectedDays(actualDays);
      if (typeof onApply === "function") onApply(iso, actualDays, selectedStartTime, selectedEndTime);
      setIsDatePickerOpen(false);
      setCalendarSelectionDays(actualDays);
    },
    [selectedEndDate, selectedStartTime, selectedEndTime, onApply]
  );

  const handlePrevMonth = useCallback(() => {
    setVisibleMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setVisibleMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }, []);

  const openPicker = useCallback(() => {
    setIsDatePickerOpen((prev) => {
      if (!prev) {
        const syncPreset = selectedDays === 30 ? 30 : selectedDays === 1 ? 1 : 7;
        setCalendarSelectionDays(syncPreset);
        const end = selectedEndDate ? new Date(selectedEndDate + "T12:00:00") : new Date();
        if (!isNaN(end.getTime())) setVisibleMonth(new Date(end.getFullYear(), end.getMonth(), 1));
      }
      return !prev;
    });
  }, [selectedDays, selectedEndDate]);

  return {
    selectedEndDate,
    setSelectedEndDate,
    selectedDays,
    setSelectedDays,
    selectedStartTime,
    setSelectedStartTime,
    selectedEndTime,
    setSelectedEndTime,
    getCurrentRange,
    getDateRangeLabel,
    isDatePickerOpen,
    setIsDatePickerOpen,
    openPicker,
    visibleMonth,
    setVisibleMonth,
    calendarSelectionDays,
    setCalendarSelectionDays,
    pendingDateForTime,
    setPendingDateForTime,
    buildMonthMatrix,
    isDateInRange,
    isRangeBoundary,
    handleCalendarDayClick,
    handleApplyDateWithTime,
    handleQuickDateFilter,
    handlePrevMonth,
    handleNextMonth,
  };
}
